/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ICommerceApiSettings } from '@msdyn365-commerce/core-internal';
import { ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import classnames from 'classnames';
import * as React from 'react';

import { ISwatchItem, SwatchItemComponent } from './swatch-item';

/**
 * Swatch item configuration.
 */
export interface ISwatchProps {
    className?: string;
    apiSettings: ICommerceApiSettings;

    list: ISwatchItem[];
    isSelectionEnabled?: boolean;
    isRefineItem?: boolean;

    onSelectItem?(item: ISwatchItem): void;
}

/**
 * Represents a single item for swatch.
 * @param props - Item configuration.
 * @returns React node.
 */
export const SwatchComponent: React.FC<ISwatchProps> = (props: ISwatchProps) => {
    const containerClassName = 'msc-swatch-container';

    const [selectedItemId, setSelectedItemId] = React.useState<string | undefined>(undefined);
    const [didUserSetAnyItem, setDidUserSetAnyItem] = React.useState<boolean>(false);

    const isSelectionEnabled = props.isSelectionEnabled ?? true;

    const onSelectItemCallback = props.onSelectItem;

    const onSelectItem = React.useCallback(
        (item: ISwatchItem) => {
            setSelectedItemId(item.itemId);
            if (!ObjectExtensions.isNullOrUndefined(onSelectItemCallback)) {
                onSelectItemCallback(item);
            }
            setDidUserSetAnyItem(true);
        },
        [setSelectedItemId, onSelectItemCallback]
    );

    if (!didUserSetAnyItem) {
        const defaultItem = props.list.find(item => item.isDefault);
        if (defaultItem && defaultItem.itemId !== selectedItemId) {
            setSelectedItemId(defaultItem.itemId);
            if (!ObjectExtensions.isNullOrUndefined(onSelectItemCallback)) {
                onSelectItemCallback(defaultItem);
            }
        }
    }
    return (
        <div
            role='radiogroup'
            className={classnames(containerClassName, props.className)}
            {...(props.isRefineItem ? { 'aria-hidden': true } : null)}
        >
            {props.list.map(item => {
                const itemId = item.itemId;
                const isSelected = selectedItemId === itemId;

                return (
                    <SwatchItemComponent
                        key={`${itemId}-${item.isDisabled ? 'disabled' : 'enabled'}`}
                        className={`${containerClassName}__item`}
                        item={item}
                        onSelectItem={onSelectItem}
                        isSelected={isSelected}
                        apiSettings={props.apiSettings}
                        isSelectionEnabled={isSelectionEnabled}
                        isRefineItem={props.isRefineItem}
                    />
                );
            })}
        </div>
    );
};
